import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import {
  type EntityType,
  getSession,
  makeEntitiesSelector,
  makeItemSelector,
  onLogout,
  setItem,
  startRequest,
  useAppDispatch,
  useAppSelector,
} from '@wappsto/redux';
import equal from 'deep-equal';
import usePrevious from './usePrevious';
import { ITEMS_PER_SLICE, STATUS } from '../util';

const itemName = 'useIds_status';

interface CacheEntry {
  status: string;
  query: any;
}

let cache: Record<string, CacheEntry> = {};
onLogout(() => {
  cache = {};
});

function setCacheStatus(dispatch: any, ids: string[], status: string, query: any): void {
  ids.forEach((id) => {
    cache[id] = { status, query };
  });
  dispatch(setItem(itemName, { ...cache }));
}

function sendGetIds(
  store: any,
  ids: string[],
  service: string,
  query: any,
  sliceLength: number,
): void {
  const res: string[][] = [];
  const slices = Math.ceil(ids.length / sliceLength);
  for (let i = 0; i < slices; i += 1) {
    res.push(ids.slice(i * sliceLength, (i + 1) * sliceLength));
  }
  const state = store.getState();
  const session = getSession(state);
  res.forEach((arr) => {
    const options = {
      url: `/${service}`,
      method: 'GET',
      query: {
        ...query,
        id: arr,
      },
    };
    const promise = startRequest(store.dispatch, options, session);
    promise
      .then((result: any) => {
        setCacheStatus(store.dispatch, arr, result.ok ? STATUS.SUCCESS : STATUS.ERROR, query);
      })
      .catch(() => {
        setCacheStatus(store.dispatch, arr, STATUS.ERROR, query);
      });
  });
}

export default function useIds(
  service: EntityType,
  ids: string[],
  query: any = {},
  sliceLength: number = ITEMS_PER_SLICE,
) {
  const store = useStore();
  const [status, setStatus] = useState<string>(STATUS.IDLE);
  const prevStatus = usePrevious(status);
  const prevIds = usePrevious(ids);
  const missingIds = useRef<string[]>([]);
  const dispatch = useAppDispatch();
  const [items, setItems] = useState<any[]>([]);
  const getEntities = useMemo(() => makeEntitiesSelector(), []);
  const getItem = useMemo(() => makeItemSelector(), []);
  const cacheItems = useAppSelector(state => getEntities(state, service, ids));
  const idsStatus = useAppSelector(state => getItem(state, itemName));

  const updateMissingIds = useCallback(() => {
    const arr: string[] = [];
    const cacheIds: string[] = [];
    ids.forEach((id) => {
      const cid = cache[id];
      if (cid) {
        const cidQ = { ...cid.query, expand: null };
        const cQ = { ...query, expand: null };
        if (
          !equal(cidQ, cQ) ||
          cid.query.expand < query.expand ||
          cid.status === STATUS.ERROR ||
          cid.status === STATUS.IDLE
        ) {
          arr.push(id);
        }
      } else if (!query || !query.expand || query.expand === 0) {
        const found = cacheItems.find((item: any) => item.meta.id === id);
        if (found) {
          cacheIds.push(id);
        } else {
          arr.push(id);
        }
      } else {
        arr.push(id);
      }
    });
    const prevMissingIds = missingIds.current;
    missingIds.current = arr;
    if (cacheIds.length > 0) {
      setCacheStatus(dispatch, cacheIds, STATUS.SUCCESS, query);
    }

    return prevMissingIds;
  }, [ids, cacheItems, dispatch, query]);

  const getMissingIds = useCallback(
    (checkIds = true) => {
      if (checkIds && equal(ids, prevIds)) {
        return;
      }
      updateMissingIds();
      if (missingIds.current.length > 0) {
        setCacheStatus(dispatch, missingIds.current, STATUS.PENDING, query);
        sendGetIds(store, missingIds.current, service, query, sliceLength);
      }
    },
    [dispatch, ids, prevIds, query, service, sliceLength, store, updateMissingIds],
  );

  useEffect(() => {
    getMissingIds();

    if (status !== STATUS.SUCCESS || prevIds !== ids) {
      for (let i = 0; i < ids.length; i += 1) {
        const idStatus = cache[ids[i]] && cache[ids[i]].status;
        if (idStatus === STATUS.ERROR) {
          setStatus(STATUS.ERROR);
          return;
        }
        if (idStatus === STATUS.PENDING) {
          setStatus(STATUS.PENDING);
          return;
        }
      }
      setStatus(STATUS.SUCCESS);
      setItems(cacheItems);
    } else if (prevStatus !== STATUS.SUCCESS && status === STATUS.SUCCESS) {
      setItems(cacheItems);
    }

    if (status === STATUS.SUCCESS) {
      setItems(cacheItems);
    }
  }, [
    dispatch,
    service,
    updateMissingIds,
    ids,
    idsStatus,
    cacheItems,
    getMissingIds,
    prevIds,
    prevStatus,
    status,
  ]);

  const reset = useCallback(() => {
    setCacheStatus(dispatch, ids, STATUS.IDLE, query);
  }, [ids, dispatch, query]);

  const refresh = useCallback(
    (callReset = true) => {
      if (callReset) {
        reset();
      }
      getMissingIds(false);
    },
    [getMissingIds, reset],
  );

  return { items, status, setStatus, reset, refresh };
}
